import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { loadProject, saveProject, generatePoster, uploadThumbnail } from "@/services/projectService";
import { getScripts } from "@/services/scriptService";
import { getCharacters } from "@/services/characterService";
import { getLocations } from "@/services/locationService";
import { getStoryboardFrames, exportShotListCsv, getAutoStoryboardProgress } from "@/services/shotService";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  IconScript, IconCharacters, IconLocations, IconScenes,
  IconStoryboard, IconSettings,
  IconSchedule, IconBudget, IconTeam, IconGreenlight
} from "@/components/Icons";
import { Logo1895 } from "@/components/Logo1895";
import SplashLoader from "@/components/SplashLoader";
import { ArrowLeft, ChevronRight, Upload, Download, Loader2, Sparkles, Image } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import NotificationPanel, { NotificationBell } from "@/components/NotificationPanel";
import { useNotifications, NOTIFICATION_TYPES, NOTIFICATION_CATEGORIES } from "@/contexts/NotificationContext";
import { PermissionsProvider, usePermissions } from "@/contexts/PermissionsContext";
import { CollaborationProvider } from "@/contexts/CollaborationContext";
import CollaborationBar from "@/components/CollaborationBar";
import VaultIndicator from "@/components/VaultIndicator";
import AutoStoryboardPreflight from "@/components/AutoStoryboardPreflight";
import AutoStoryboardProgress from "@/components/AutoStoryboardProgress";

// Import page components
import ScriptPage from "@/components/pages/ScriptPage";
import CharactersPage from "@/components/pages/CharactersPage";
import LocationsPage from "@/components/pages/LocationsPage";
import ScenesPage from "@/components/pages/ScenesPage";
import StoryboardPage from "@/components/pages/StoryboardPage";
import SchedulePage from "@/components/pages/SchedulePage";
import BudgetPage from "@/components/pages/BudgetPage";
import TeamPage from "@/components/pages/TeamPage";
import GreenlightPage from "@/components/pages/GreenlightPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NAV_ITEMS = [
  { id: "script", label: "Script", icon: IconScript, shortcut: "1", permission: "view_all" },
  { id: "characters", label: "Characters", icon: IconCharacters, shortcut: "2", permission: "view_all" },
  { id: "locations", label: "Locations", icon: IconLocations, shortcut: "3", permission: "view_all" },
  { id: "scenes", label: "Scenes", icon: IconScenes, shortcut: "4", permission: "view_all" },
  { id: "storyboard", label: "Storyboard", icon: IconStoryboard, shortcut: "5", permission: "view_all" },
  { id: "schedule", label: "Schedule", icon: IconSchedule, shortcut: "6", permission: "budget_scheduling" },
  { id: "budget", label: "Budget", icon: IconBudget, shortcut: "7", permission: "budget_scheduling" },
  { id: "team", label: "Team", icon: IconTeam, shortcut: "8", permission: "view_all" },
  { id: "greenlight", label: "Greenlight", icon: IconGreenlight, shortcut: "9", permission: "view_all" },
];


function PermissionFilteredNav({ activeTab, characters, locations, script, frames, onTabChange, getUnreadByCategory, autoGenActive }) {
  const { can } = usePermissions();
  return NAV_ITEMS.filter(item => can(item.permission)).map((item) => {
    const Icon = item.icon;
    const isActive = activeTab === item.id;
    const count = item.id === "characters" ? characters.length
               : item.id === "locations" ? locations.length
               : item.id === "scenes" ? (script?.scenes?.length || 0)
               : item.id === "storyboard" ? frames.length
               : null;
    return (
      <Tooltip key={item.id}>
        <TooltipTrigger asChild>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onTabChange(item.id)}
            className={`nav-rail-item ${isActive ? 'active' : ''} relative`}
            data-testid={`nav-${item.id}`}
          >
            <Icon size={22} active={isActive} />
            {count !== null && count > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-[var(--color-gold-500)] text-[var(--color-base-950)] text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                {count > 99 ? '99+' : count}
              </span>
            )}
            {item.id === "storyboard" && autoGenActive && (
              <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-[var(--color-gold-400)] rounded-full animate-pulse" />
            )}
            {getUnreadByCategory(item.id) > 0 && (
              <span className="absolute top-0 right-0 w-2 h-2 bg-[var(--color-error)] rounded-full animate-pulse" />
            )}
          </motion.button>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={8} className="bg-[var(--bg-overlay)] border-[var(--border-default)] rounded-lg px-3 py-2 text-[var(--text-primary)]">
          <div className="flex items-center gap-3">
            <p className="text-sm font-medium">{item.label}</p>
            <span className="text-xs text-[var(--text-disabled)] bg-[var(--bg-muted)] px-1.5 py-0.5 rounded">{item.shortcut}</span>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  });
}


export default function ProjectWorkspace() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("script");
  const [project, setProject] = useState(null);
  const [script, setScript] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [locations, setLocations] = useState([]);
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editDesc, setEditDesc] = useState("");
  const [generatingPoster, setGeneratingPoster] = useState(false);
  const [showAutoGenPreflight, setShowAutoGenPreflight] = useState(false);
  const [showAutoGenProgress, setShowAutoGenProgress] = useState(false);

  const { markCategoryAsRead, getUnreadByCategory } = useNotifications();
  
  // Mark notifications as read when switching tabs
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    
    // Map tab IDs to notification categories
    const tabToCategory = {
      'script': NOTIFICATION_CATEGORIES.SCRIPT,
      'characters': NOTIFICATION_CATEGORIES.CHARACTERS,
      'locations': NOTIFICATION_CATEGORIES.LOCATIONS,
      'scenes': NOTIFICATION_CATEGORIES.SCENES,
      'storyboard': NOTIFICATION_CATEGORIES.STORYBOARD
    };
    
    const category = tabToCategory[tabId];
    if (category) {
      markCategoryAsRead(category);
    }
  }, [markCategoryAsRead]);

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      const num = parseInt(e.key);
      if (num >= 1 && num <= NAV_ITEMS.length) {
        handleTabChange(NAV_ITEMS[num - 1].id);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleTabChange]);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      const [projectData, scriptsData, charsData, locsData, framesData] = await Promise.all([
        loadProject(projectId),
        getScripts(projectId),
        getCharacters(projectId),
        getLocations(projectId),
        getStoryboardFrames(projectId),
      ]);

      setProject(projectData);
      setScript(scriptsData[0] || null);
      setCharacters(charsData);
      setLocations(locsData);
      setFrames(framesData);

      // Check if there's an active auto-storyboard generation
      try {
        const progressData = await getAutoStoryboardProgress(projectId);
        if (["preflight", "stage_1", "stage_2", "stage_3"].includes(progressData.status)) {
          setShowAutoGenProgress(true);
        }
      } catch {
        // No active generation — that's fine
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  // Silent background refresh — never shows SplashLoader
  const refreshData = useCallback(async () => {
    try {
      const [projectData, scriptsData, charsData, locsData, framesData] = await Promise.all([
        loadProject(projectId),
        getScripts(projectId),
        getCharacters(projectId),
        getLocations(projectId),
        getStoryboardFrames(projectId),
      ]);
      setProject(projectData);
      setScript(scriptsData[0] || null);
      setCharacters(charsData);
      setLocations(locsData);
      setFrames(framesData);
    } catch (error) {
      console.error("Background refresh failed:", error);
    }
  }, [projectId]);

  if (loading) {
    return <SplashLoader message="Loading project..." />;
  }

  const renderPage = () => {
    const pageProps = {
      projectId,
      project,
      script,
      characters,
      locations,
      frames,
      scenes: script?.scenes || [],
      onUpdate: refreshData,
    };

    switch (activeTab) {
      case "script":
        return <ScriptPage {...pageProps} />;
      case "characters":
        return <CharactersPage {...pageProps} />;
      case "locations":
        return <LocationsPage {...pageProps} />;
      case "scenes":
        return <ScenesPage {...pageProps} />;
      case "storyboard":
        return <StoryboardPage {...pageProps} autoGenActive={showAutoGenProgress} onAutoGenDismiss={() => setShowAutoGenProgress(false)} />;
      case "schedule":
        return <SchedulePage {...pageProps} />;
      case "budget":
        return <BudgetPage {...pageProps} />;
      case "team":
        return <TeamPage {...pageProps} />;
      case "greenlight":
        return <GreenlightPage projectId={projectId} script={script} project={project} />;
      default:
        return null;
    }
  };

  const currentNavItem = NAV_ITEMS.find(item => item.id === activeTab);

  const handleRegeneratePoster = async () => {
    setGeneratingPoster(true);
    try {
      if (editDesc.trim() && editDesc !== project?.description) {
        await saveProject(projectId, { description: editDesc });
      }
      await generatePoster(projectId);
      toast.success("Poster generated!");
      refreshData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Poster generation failed");
    } finally {
      setGeneratingPoster(false);
    }
  };

  const handleUploadThumbnail = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result.split(',')[1];
      try {
        await uploadThumbnail(projectId, { thumbnail: base64 });
        toast.success("Thumbnail updated");
        refreshData();
      } catch {
        toast.error("Upload failed");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleExportCSV = async () => {
    try {
      const csvData = await exportShotListCsv(projectId);
      const blob = new Blob([typeof csvData === 'string' ? csvData : JSON.stringify(csvData)], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shot_list_${project?.name || projectId}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Shot list exported!");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <CollaborationProvider projectId={projectId}>
    <PermissionsProvider projectId={projectId}>
    <TooltipProvider delayDuration={300}>
      <div className="h-screen bg-[var(--bg-app)] flex overflow-hidden">
        {/* Premium Navigation Rail */}
        <motion.nav 
          initial={{ x: -72 }}
          animate={{ x: 0 }}
          className="nav-rail flex-shrink-0 z-30"
        >
          {/* Logo */}
          <div className="mb-8">
            <Logo1895 variant="icon" width={32} />
          </div>
          
          {/* Back Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/")}
                className="nav-rail-item mb-6"
                data-testid="back-to-projects"
              >
                <ArrowLeft size={20} />
              </motion.button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8} className="bg-[var(--bg-overlay)] border-[var(--border-default)] rounded-lg px-3 py-2 text-[var(--text-primary)]">
              <p className="text-sm">Back to Projects</p>
            </TooltipContent>
          </Tooltip>

          {/* Divider */}
          <div className="w-8 h-px bg-[var(--border-default)] mb-6" />

          {/* Navigation Items */}
          <div className="flex flex-col gap-2 flex-1">
            <PermissionFilteredNav
              activeTab={activeTab}
              characters={characters}
              locations={locations}
              script={script}
              frames={frames}
              onTabChange={handleTabChange}
              getUnreadByCategory={getUnreadByCategory}
              autoGenActive={showAutoGenProgress}
            />
          </div>

          {/* Bottom Actions */}
          <div className="mt-auto flex flex-col gap-2">
            {/* Auto Generate Button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAutoGenPreflight(true)}
                  disabled={!script}
                  className={`nav-rail-item bg-gradient-to-br from-[var(--color-gold-400)] to-[var(--color-gold-600)] text-[var(--color-base-950)] ${!script ? 'opacity-40 cursor-not-allowed' : ''}`}
                  style={{ boxShadow: script ? 'var(--glow-gold)' : 'none' }}
                  data-testid="auto-generate-btn"
                >
                  <Sparkles size={20} />
                </motion.button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8} className="bg-[var(--bg-overlay)] border-[var(--border-default)] rounded-lg px-3 py-2 text-[var(--text-primary)]">
                <p className="text-sm">Auto Generate Full Storyboard</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="nav-rail-item"
                  data-testid="notification-btn"
                >
                  <NotificationBell />
                </motion.button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8} className="bg-[var(--bg-overlay)] border-[var(--border-default)] rounded-lg px-3 py-2 text-[var(--text-primary)]">
                <p className="text-sm">Notifications</p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => { setShowSettings(true); setEditDesc(project?.description || ""); }}
                  className="nav-rail-item"
                  data-testid="settings-btn"
                >
                  <IconSettings size={20} />
                </motion.button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8} className="bg-[var(--bg-overlay)] border-[var(--border-default)] rounded-lg px-3 py-2 text-[var(--text-primary)]">
                <p className="text-sm">Project Settings</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </motion.nav>
        
        {/* Notification Panel */}
        <NotificationPanel 
          isOpen={showNotifications} 
          onClose={() => setShowNotifications(false)}
        />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top Bar */}
          <header className="h-14 px-6 border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-4">
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[var(--text-muted)]">{project?.name}</span>
                <ChevronRight size={14} className="text-[var(--text-disabled)]" />
                <span className="text-[var(--text-primary)] font-medium">{currentNavItem?.label}</span>
              </div>
            </div>
            
            {/* Generation Progress */}
            <div className="flex items-center gap-4">
              {/* Vault Status */}
              <VaultIndicator />
              {/* Collaboration Bar */}
              <div className="relative">
                <CollaborationBar />
              </div>
              
              <button className="btn-ghost btn-icon-sm" onClick={handleExportCSV} title="Export Shot List CSV" data-testid="export-csv-btn">
                <Download size={18} />
              </button>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                className="h-full"
              >
                {renderPage()}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>

      {/* Project Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-[var(--bg-surface)] border-[var(--border-default)] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg text-[var(--text-primary)]">Project Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Thumbnail */}
            <div>
              <label className="label mb-3 block">Movie Poster / Thumbnail</label>
              <div className="flex gap-4">
                <div className="w-32 aspect-[2/3] rounded-xl overflow-hidden bg-[var(--bg-muted)] border border-[var(--border-subtle)] flex-shrink-0">
                  {project?.thumbnail ? (
                    <img src={`data:image/jpeg;base64,${project.thumbnail}`} alt="Poster" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image size={32} className="text-[var(--text-disabled)]" />
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <Button onClick={handleRegeneratePoster} disabled={generatingPoster} className="btn-primary w-full text-xs" size="sm" data-testid="regenerate-poster-btn">
                    {generatingPoster ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    <span className="ml-1.5">{project?.thumbnail ? 'Regenerate Poster' : 'Generate Poster'}</span>
                  </Button>
                  <label className="btn-secondary flex items-center justify-center gap-1.5 text-xs w-full py-2 rounded-lg cursor-pointer" data-testid="upload-thumbnail-btn">
                    <Upload size={14} />
                    <span>Upload Image</span>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadThumbnail(e.target.files?.[0])} />
                  </label>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="label mb-2 block">Description</label>
              <Textarea
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                className="input resize-none h-24"
                placeholder="Project description for poster generation..."
                data-testid="settings-description"
              />
              <p className="text-[10px] text-[var(--text-disabled)] mt-1">Change description and regenerate to update the poster</p>
            </div>

            {/* Export */}
            <div>
              <label className="label mb-2 block">Export</label>
              <Button onClick={handleExportCSV} className="btn-secondary w-full text-xs" size="sm" data-testid="export-csv-settings-btn">
                <Download size={14} />
                <span className="ml-1.5">Export Shot List (CSV)</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </TooltipProvider>

    <AutoStoryboardPreflight
      open={showAutoGenPreflight}
      onOpenChange={setShowAutoGenPreflight}
      projectId={project?.id}
      sceneNumber={null}
      onGenerationStarted={() => {
        setShowAutoGenProgress(true);
      }}
    />

    {/* Show floating progress ONLY when NOT on storyboard tab */}
    {showAutoGenProgress && project?.id && activeTab !== "storyboard" && (
      <AutoStoryboardProgress
        projectId={project.id}
        onComplete={() => {
          setShowAutoGenProgress(false);
          refreshData();
          toast.success("Full storyboard generation complete!");
        }}
        onDismiss={() => setShowAutoGenProgress(false)}
      />
    )}

    </PermissionsProvider>
    </CollaborationProvider>
  );
}
